import React from 'react';
import { TiltCard } from '../../components/TiltCard/TiltCard';
import { RippleButton } from '../../components/RippleButton/RippleButton';
import './Modules.scss';

const modules = [
  {
    id: 1,
    name: 'Accounting',
    description: 'Complete financial management with automated invoicing, expense tracking, and real-time reporting.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    color: '#10B981',
    features: ['Invoicing', 'Expense Tracking', 'Tax Reports', 'Multi-currency'],
    status: 'installed',
  },
  {
    id: 2,
    name: 'Point of Sale',
    description: 'Modern POS system with inventory sync, multiple payment methods, and receipt management.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="5" width="20" height="14" rx="2"/>
        <line x1="2" y1="10" x2="22" y2="10"/>
      </svg>
    ),
    color: '#6366F1',
    features: ['Inventory Sync', 'Multi-payment', 'Receipts', 'Analytics'],
    status: 'installed',
  },
  {
    id: 3,
    name: 'E-Commerce',
    description: 'Build and manage your online store with product catalogs, checkout, and order fulfillment.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1"/>
        <circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
      </svg>
    ),
    color: '#EC4899',
    features: ['Product Catalog', 'Shopping Cart', 'Payment Gateway', 'Shipping'],
    status: 'available',
  },
  {
    id: 4,
    name: 'Website Builder',
    description: 'Create stunning websites with drag-and-drop builder, templates, and SEO tools.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="21" x2="9" y2="9"/>
      </svg>
    ),
    color: '#F59E0B',
    features: ['Drag & Drop', 'Templates', 'SEO Tools', 'Analytics'],
    status: 'installed',
  },
  {
    id: 5,
    name: 'Social Media',
    description: 'Plan, schedule, and analyze your social media presence across all platforms.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
    color: '#8B5CF6',
    features: ['Scheduler', 'Analytics', 'Content Calendar', 'Auto-post'],
    status: 'available',
  },
  {
    id: 6,
    name: 'AI Studio',
    description: 'Generate content, images, and automate marketing with cutting-edge AI tools.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
        <path d="M8.5 8.5v.01"/>
        <path d="M16 15v.01"/>
        <path d="M12 12v.01"/>
        <path d="M11 17v.01"/>
        <path d="M7 14v.01"/>
      </svg>
    ),
    color: '#00D4AA',
    features: ['Text Generation', 'Image AI', 'Marketing Automation', 'Chatbot'],
    status: 'premium',
  },
  {
    id: 7,
    name: 'CRM',
    description: 'Manage customer relationships, track leads, and automate sales pipelines.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    color: '#3B82F6',
    features: ['Lead Tracking', 'Pipeline', 'Email Integration', 'Reports'],
    status: 'available',
  },
  {
    id: 8,
    name: 'HR Management',
    description: 'Complete HR solution with payroll, attendance, and performance tracking.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="7" width="20" height="14" rx="2"/>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>
    ),
    color: '#EF4444',
    features: ['Payroll', 'Attendance', 'Performance', 'Recruitment'],
    status: 'available',
  },
];

export const Modules: React.FC = () => {
  return (
    <div className="modules-page">
      <div className="page-header">
        <h1 className="page-title">Modules</h1>
        <p className="page-subtitle">Explore and manage your business modules.</p>
      </div>

      <div className="modules-grid">
        {modules.map((module, index) => (
          <TiltCard 
            key={module.id} 
            className={`module-card ${module.status}`}
            shimmer
            spotlight
          >
            <div className="module-header">
              <div 
                className="module-icon-large"
                style={{ background: `${module.color}20`, color: module.color }}
              >
                {module.icon}
              </div>
              <div className={`status-indicator ${module.status}`}>
                {module.status === 'installed' && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                )}
                {module.status === 'premium' && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                  </svg>
                )}
                {module.status === 'available' && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                )}
              </div>
            </div>
            
            <h3 className="module-name">{module.name}</h3>
            <p className="module-description">{module.description}</p>
            
            <div className="module-features">
              {module.features.map((feature, idx) => (
                <span key={idx} className="feature-tag">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {feature}
                </span>
              ))}
            </div>
            
            <div className="module-actions">
              {module.status === 'installed' ? (
                <RippleButton variant="primary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  Open
                </RippleButton>
              ) : module.status === 'premium' ? (
                <RippleButton>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                    <path d="M4 22h16"/>
                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                  </svg>
                  Upgrade
                </RippleButton>
              ) : (
                <RippleButton>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Install
                </RippleButton>
              )}
              <RippleButton className="outline">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              </RippleButton>
            </div>
          </TiltCard>
        ))}
      </div>
    </div>
  );
};
